import { isDefined } from '@/composables/utils'
import type { ITableHeader } from '@/interfaces/ITableHeader'
import { useAppStore } from '@/stores/ui/AppStore'
import { type Composer } from 'vue-i18n'
import { CustomFieldRecord } from '../CustomFieldRecord'

/**
 * Get the label for the autoComplete field.
 * @param i18n The i18n instance for translation.
 * @param item The CustomFieldRecord item.
 * @returns
 */
function getAutoCompleteLabel(
  i18n: Composer<{}, {}, {}, string, never, string>,
  item: CustomFieldRecord
): string {
  if (item.combo === 1) {
    return item.autoComplete ? i18n.t('labels.yes') : i18n.t('labels.no')
  }
  return i18n.t('labels.notApplicable')
}

export const getTableHeaders = (
  i18n: Composer<{}, {}, {}, string, never, string>
): Array<ITableHeader> => {
  const appStore = useAppStore()
  const _fieldNamesMap = [
    i18n.t('fields.people.company'),
    i18n.t('fields.people.location'),
    i18n.t('fields.people.custom1Data'),
    i18n.t('fields.people.custom2Data'),
    i18n.t('fields.people.custom3Data'),
    i18n.t('fields.people.custom4Data')
  ]
  return [
    {
      title: i18n.t('fields.customFields.labelName'),
      key: 'labelName',
      align: 'start',
      nowrap: true,
      sortable: false,
      value: (item: CustomFieldRecord) => {
        switch (item.row) {
          case 0: {
            return i18n.t('fields.people.company')
          }
          case 1: {
            return i18n.t('fields.people.location')
          }
          default: {
            return item.labelName
          }
        }
      }
    },
    {
      title: i18n.t('fields.customFields.fieldName'),
      key: 'fieldName',
      align: 'start',
      nowrap: true,
      sortable: false,
      value: (item: CustomFieldRecord) => {
        if (isDefined(item.row)) {
          return _fieldNamesMap[item.row]
        }
        return '-'
      }
    },
    {
      title: i18n.t('fields.customFields.required'),
      key: 'required',
      value: (item: CustomFieldRecord) =>
        `${item.required ? i18n.t('labels.yes') : i18n.t('labels.no')}`,
      align: 'start',
      sortable: false,
      nowrap: true
    },
    {
      title: i18n.t('fields.customFields.autoComplete'),
      key: 'autoComplete',
      value: (item: CustomFieldRecord) => getAutoCompleteLabel(i18n, item),
      align: 'start',
      nowrap: true,
      sortable: false
    },
    {
      title: i18n.t('fields.customFields.allowFiltering'),
      key: 'allowFiltering',
      value: (item: CustomFieldRecord) =>
        `${item.allowFiltering ? i18n.t('labels.yes') : i18n.t('labels.no')}`,
      align: 'start',
      nowrap: true,
      sortable: false
    },
    {
      title: i18n.t('fields.customFields.type'),
      key: 'combo',
      value: (item: CustomFieldRecord) =>
        `${item.combo ? i18n.t('fields.customFields.values.textInput') : i18n.t('fields.customFields.values.dropdown')}`,
      align: 'start',
      sortable: false,
      nowrap: true
    },
    {
      title: i18n.t('fields.customFields.options'),
      key: 'options',
      value: (item: CustomFieldRecord) => {
        const options: Array<string> = []
        const keys = Object.keys(item).filter((key) => {
          return key.startsWith('option')
        })

        keys.forEach((key: string) => {
          if (item[key] && item[key].length > 0) {
            options.push(item[key])
          }
        })
        return options.sort((a, b) => a.localeCompare(b))
      },
      align: 'start',
      sortable: false,
      nowrap: false
    },
    {
      title: i18n.t('fields.actions'),
      key: 'actions',
      align: 'start',
      nowrap: true,
      sortable: false,
      headerProps: { hidden: !appStore.isProgramAdmin }
    }
  ]
}
